import { InvalidTokenError, jwtDecode } from "jwt-decode";

import { Role, ROLES } from "@/lib/auth/roles";
import { UnableToReadTokenError } from '@/lib/types/errors'
import { Token } from "@/types/token"

export const LocalStorageKeys = {
  ROLES: 'roles',
  REFRESH_TOKEN: 'refresh_token',
  TOKEN: 'token',
  LAST_REFRESHED: 'last_refreshed',
} as const

const UNREADABLE_TOKEN = 'Unable to read token. Is it a valid JWT or JSON object?'

/** A Token is always a plain object; a payload that decodes to any other shape is not one. */
const isTokenObject = (value: unknown): value is Token =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/** Null unless the value parses to a plain object, so callers never receive a scalar as a Token. */
const parseTokenObject = (value: string): Token | null => {
  try {
    const parsed: unknown = JSON.parse(value)

    return isTokenObject(parsed) ? parsed : null
  } catch {
    return null
  }
}

/**
 * Returns a Token or throws UnableToReadTokenError — no other error type, and no non-object
 * masquerading as a Token. That guarantee matters now the terms gate calls this from a route
 * beforeLoad: the router's defaultOnCatch (main.tsx) recognises this one error type and answers it
 * by clearing storage and bouncing to login. Anything else lands on the generic error page.
 */
export const getDecodedToken = (): Token => {
  const token = localStorage.getItem(LocalStorageKeys.TOKEN)

  if (token === null) {
    throw new UnableToReadTokenError('No token stored in the browser')
  }

  try {
    const decoded = jwtDecode<Token>(token)

    // A payload that base64-decodes cleanly but JSON-parses to a scalar raises no error here.
    if (isTokenObject(decoded)) {
      return decoded
    }
  } catch (error: any) {
    if (error instanceof InvalidTokenError) {
      if (error.message.includes('invalid base64 for part #2')) {
        // Token must be a stringified object, e.g. '{"username": "blah"}', so parse it back to a regular object.
        // Object-checked rather than returned raw: a dotted-but-unparseable token ("1.2.3") escaped
        // here as a SyntaxError, and a parseable scalar ("1.5") as the number 1.5 typed as a Token.
        const parsed = parseTokenObject(token)

        if (parsed !== null) {
          return parsed
        }
      }
    }
  }

  throw new UnableToReadTokenError(UNREADABLE_TOKEN)
}

export function storeTokenData({
  token,
  refresh_token,
}: {
  token: string
  /** Absent for sessions that are not issued one, such as a 2FA-interim session. */
  refresh_token?: string
}): void {
  localStorage.setItem(LocalStorageKeys.TOKEN, token)

  if (refresh_token) {
    localStorage.setItem(LocalStorageKeys.REFRESH_TOKEN, refresh_token)
  } else {
    // Storing an absent value would persist the string "undefined" and be mistaken for a usable token.
    localStorage.removeItem(LocalStorageKeys.REFRESH_TOKEN)
  }

  try {
    localStorage.setItem(LocalStorageKeys.ROLES, JSON.stringify(getDecodedToken().roles))
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_error: any) {
    localStorage.setItem(LocalStorageKeys.ROLES, '[]')
  }

  localStorage.setItem(LocalStorageKeys.LAST_REFRESHED, Date.now().toString())
}

export const getRoles = (): Role[] => {
  try {
    const roles = localStorage.getItem(LocalStorageKeys.ROLES)

    return roles ? JSON.parse(roles) : []
  } catch {
    return []
  }
}

export const getToken = (): string | null => {
  return localStorage.getItem(LocalStorageKeys.TOKEN)
}

export const getRefreshToken = (): string | null => {
  return localStorage.getItem(LocalStorageKeys.REFRESH_TOKEN)
}

/**
 * Whether the user has accepted our terms (VFX-3096), read from the access token claim.
 *
 * Fails open on an absent claim, deliberately: tokens minted before the backend half of VFX-3096
 * deploys carry no claim, and that is what lets the gate ship with no feature flag.
 *
 * Self-limiting rather than permanent — an expired token is refused before it ever reaches here, so
 * once AUTH_TOKEN_TTL has elapsed since the backend deploy there is no live token left without the
 * claim and this branch is unreachable. Flipping it to fail closed is worth doing once that has
 * passed, and would also remove this function's unstated dependence on _auth.tsx checking 2FA
 * first: the interim 2FA token carries no terms claim either, so it would otherwise be waved
 * through here. Left for another future task rather than done now — flipping it early would gate
 * every live session at once, which is the outcome the branch exists to prevent.
 */
export const isTermsAccepted = (): boolean => {
  // Deliberately not guarded against an unreadable token. That is not a terms question, and
  // catching it here would hide it from the router's defaultOnCatch (main.tsx), which clears storage
  // and bounces to login — a better answer than waving the user past the gate on a token every
  // subsequent request will 401 on anyway.
  const decoded = getDecodedToken()

  if (!('terms_accepted_at' in decoded)) {
    return true
  }

  return Boolean(decoded.terms_accepted_at)
}

export const isTwoFactorInProgress = (): boolean => {
  return getRoles().includes(ROLES.IS_AUTHENTICATED_2FA_IN_PROGRESS)
}

export const clearTokenData = (): void => {
  localStorage.removeItem(LocalStorageKeys.TOKEN)
  localStorage.removeItem(LocalStorageKeys.ROLES)
  localStorage.removeItem(LocalStorageKeys.REFRESH_TOKEN)
  localStorage.removeItem(LocalStorageKeys.LAST_REFRESHED)
}
